import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projetosApi, ESTADOS_PROJETO, type EstadoFase, type LinhaGantt } from '../api/projetos.js';
import { atividadesApi } from '../api/atividades.js';
import { TimelineGrid } from '../componentes/gantt/TimelineGrid.js';
import { BarraAtividade } from '../componentes/gantt/BarraAtividade.js';
import { SetasDependencia } from '../componentes/gantt/SetasDependencia.js';
import { PainelDetalheAtividade } from '../componentes/gantt/PainelDetalheAtividade.js';
import { diasEntre, somarDias, formatarDiaEixo } from '../componentes/gantt/timeline-utils.js';
import { TrilhoEstado } from '../componentes/transversais/TrilhoEstado.js';
import { ModalTransicao } from '../componentes/transversais/ModalTransicao.js';
import { BotaoComBloqueio } from '../componentes/transversais/BotaoComBloqueio.js';
import { ModalGateReview } from '../componentes/projeto/ModalGateReview.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import type { TransicaoDisponivel } from '../api/tipos.js';

const ESTADOS_FASE: EstadoFase[] = ['Pendente', 'EmAndamento', 'AguardandoGate', 'Aprovada'];

function AbaBaseline({ projetoId }: { projetoId: string }) {
  const queryClient = useQueryClient();
  const { data: baselines } = useQuery({ queryKey: ['projeto', projetoId, 'baselines'], queryFn: () => projetosApi.baselines(projetoId) });
  const [marco, setMarco] = useState('');
  const [dataMarco, setDataMarco] = useState('');

  const aprovar = useMutation({
    mutationFn: () => projetosApi.aprovarBaseline(projetoId, marco ? [{ marco, dataBaseline: dataMarco }] : [], []),
    onSuccess: () => {
      setMarco('');
      setDataMarco('');
      queryClient.invalidateQueries({ queryKey: ['projeto', projetoId] });
    },
  });

  const vigente = baselines?.at(-1);

  return (
    <div className="flex flex-col gap-4">
      {(!baselines || baselines.length === 0) && (
        <VazioQueEnsina explicacao="Nenhuma baseline aprovada ainda. A baseline v1 destrava o início da execução do projeto (I15)." />
      )}

      {baselines && baselines.length > 0 && (
        <ul className="flex flex-col gap-2">
          {baselines.map((b) => (
            <li key={b.id} className={`rounded border p-3 text-sm ${b.id === vigente?.id ? 'border-aco bg-aco/5' : 'border-borda bg-superficie'}`}>
              <p className="text-ink">
                v{b.versao} {b.id === vigente?.id && <span className="fonte-mono text-xs text-aco-700">vigente</span>}
              </p>
              <p className="text-xs text-grafite">
                origem: {b.origem} · aprovada por {b.aprovadaPor} em {b.aprovadaEm}
              </p>
              {b.datasMarcos.length > 0 && (
                <ul className="mt-1 text-xs text-grafite">
                  {b.datasMarcos.map((m, i) => (
                    <li key={i}>
                      {m.marco}: {m.dataBaseline}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          aprovar.mutate();
        }}
        className="flex flex-col gap-2 rounded border border-borda bg-superficie p-3"
      >
        <p className="text-xs font-semibold text-grafite">Nova baseline (v{(baselines?.length ?? 0) + 1})</p>
        <div className="flex gap-2">
          <input
            required
            placeholder="Nome do marco"
            value={marco}
            onChange={(e) => setMarco(e.target.value)}
            className="flex-1 rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink"
          />
          <input
            required
            type="date"
            value={dataMarco}
            onChange={(e) => setDataMarco(e.target.value)}
            className="rounded border border-borda bg-base-100 px-2 py-1.5 text-sm text-ink"
          />
        </div>
        <button type="submit" disabled={aprovar.isPending} className="self-start rounded bg-aco px-3 py-1.5 text-xs font-medium text-white hover:bg-aco-700 disabled:opacity-50">
          Aprovar baseline
        </button>
      </form>
    </div>
  );
}

function AbaGantt({ projetoId }: { projetoId: string }) {
  const queryClient = useQueryClient();
  const { data: linhas, isLoading } = useQuery({ queryKey: ['projeto', projetoId, 'gantt'], queryFn: () => projetosApi.gantt(projetoId) });
  const [fasesColapsadas, setFasesColapsadas] = useState<Set<string>>(new Set());
  const [atividadeSelecionada, setAtividadeSelecionada] = useState<LinhaGantt | null>(null);

  const editarDatas = useMutation({
    mutationFn: ({ atividadeId, inicioPrevisto, fimPrevisto }: { atividadeId: string; inicioPrevisto: string; fimPrevisto: string }) =>
      atividadesApi.editarDatas(atividadeId, inicioPrevisto, fimPrevisto),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projeto', projetoId, 'gantt'] }),
  });

  if (isLoading) return <p className="text-sm text-grafite">Carregando…</p>;
  if (!linhas || linhas.length === 0) {
    return <VazioQueEnsina explicacao="Nenhuma atividade neste projeto ainda. O Gantt se preenche conforme as fases ganham atividades." />;
  }

  const porFase = new Map<string, typeof linhas>();
  for (const linha of linhas) {
    const lista = porFase.get(linha.faseNome) ?? [];
    lista.push(linha);
    porFase.set(linha.faseNome, lista);
  }

  const linhasVisiveis = linhas.filter((l) => !fasesColapsadas.has(l.faseNome));
  const todasDatas = linhasVisiveis.flatMap((l) => [l.inicioPrevisto, l.fimPrevisto]).filter((d): d is string => Boolean(d));
  const dataMin = todasDatas.length > 0 ? todasDatas.reduce((a, b) => (a < b ? a : b)) : new Date().toISOString().slice(0, 10);
  const dataMax = todasDatas.length > 0 ? todasDatas.reduce((a, b) => (a > b ? a : b)) : dataMin;
  const dataInicioEixo = somarDias(dataMin, -3);
  const totalDias = Math.max(14, diasEntre(dataInicioEixo, dataMax) + 6);
  const diaPx = 22;
  const hoje = new Date().toISOString().slice(0, 10);
  const hojeIdx = hoje >= dataInicioEixo ? diasEntre(dataInicioEixo, hoje) : null;
  const alturaLinha = 34;

  const linhaTopoPorId = new Map<string, number>();
  let acumulado = 0;
  for (const [, linhasDaFase] of porFase) {
    acumulado += 30; // cabeçalho da fase
    for (const linha of linhasDaFase) {
      if (fasesColapsadas.has(linha.faseNome)) continue;
      linhaTopoPorId.set(linha.atividadeId, acumulado);
      acumulado += alturaLinha;
    }
  }

  function alternarFase(faseNome: string) {
    setFasesColapsadas((atual) => {
      const novo = new Set(atual);
      if (novo.has(faseNome)) novo.delete(faseNome);
      else novo.add(faseNome);
      return novo;
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[minmax(0,340px)_1fr] overflow-hidden rounded-lg border border-borda bg-superficie">
        <div className="border-r border-borda">
          <div className="grid h-10 grid-cols-[1fr_70px_56px_56px_60px_44px] items-center gap-1 border-b border-borda bg-borda/20 px-3 text-[10px] font-semibold uppercase tracking-wide text-grafite">
            <span>Atividade</span>
            <span>Status</span>
            <span>Início</span>
            <span>Fim</span>
            <span>Resp.</span>
            <span className="text-right">%</span>
          </div>
          {[...porFase.entries()].map(([faseNome, linhasDaFase]) => {
            const colapsada = fasesColapsadas.has(faseNome);
            return (
              <div key={faseNome}>
                <button
                  type="button"
                  onClick={() => alternarFase(faseNome)}
                  className="flex h-[30px] w-full items-center gap-1.5 border-b border-borda bg-borda/10 px-3 text-left text-[11px] font-bold uppercase tracking-wide text-grafite"
                >
                  <span className={`inline-block text-[9px] transition-transform ${colapsada ? '-rotate-90' : ''}`}>▾</span>
                  {faseNome}
                </button>
                {!colapsada &&
                  linhasDaFase.map((linha) => (
                    <div
                      key={linha.atividadeId}
                      className="grid h-[34px] grid-cols-[1fr_70px_56px_56px_60px_44px] items-center gap-1 border-b border-borda px-3 text-xs"
                    >
                      <span className="flex items-center gap-1 truncate pl-2">
                        {linha.grupo && <span className="fonte-mono shrink-0 rounded bg-borda/40 px-1 text-[9px] text-grafite">{linha.grupo}</span>}
                        <span className="truncate text-ink">{linha.atividadeNome}</span>
                        {linha.conflito && <span className="shrink-0 text-status-ambar" title="Conflito de precedência (R-ATV-8)">⚠</span>}
                      </span>
                      <span className="fonte-mono truncate text-[10px] text-grafite">{linha.estado}</span>
                      <span className="fonte-mono text-[10px] text-grafite">{linha.inicioPrevisto?.slice(5) ?? '—'}</span>
                      <span className="fonte-mono text-[10px] text-grafite">{linha.fimPrevisto?.slice(5) ?? '—'}</span>
                      <span className="fonte-mono text-[10px] text-grafite">—</span>
                      <span className="fonte-mono text-right text-[10px] text-grafite">{Math.round(linha.pctDerivado * 100)}%</span>
                    </div>
                  ))}
              </div>
            );
          })}
        </div>

        <div className="relative overflow-x-auto">
          <div style={{ width: totalDias * diaPx, minWidth: '100%' }}>
            <div className="relative h-10 border-b border-borda bg-borda/20">
              <TimelineGrid totalDias={totalDias} diaPx={diaPx} hojeIdx={hojeIdx} dataInicio={dataInicioEixo} />
              {Array.from({ length: totalDias }, (_, i) =>
                i % 5 === 0 ? (
                  <span key={i} className="fonte-mono absolute top-2.5 text-[9px] text-grafite" style={{ left: i * diaPx + 2 }}>
                    {formatarDiaEixo(dataInicioEixo, i)}
                  </span>
                ) : null,
              )}
            </div>
            <div className="relative">
              {[...porFase.entries()].map(([faseNome, linhasDaFase]) => {
                const colapsada = fasesColapsadas.has(faseNome);
                return (
                  <div key={faseNome}>
                    <div className="relative h-[30px] border-b border-borda bg-borda/10">
                      <TimelineGrid totalDias={totalDias} diaPx={diaPx} hojeIdx={null} dataInicio={dataInicioEixo} />
                    </div>
                    {!colapsada &&
                      linhasDaFase.map((linha) => (
                        <div key={linha.atividadeId} className="relative h-[34px] border-b border-borda">
                          <TimelineGrid totalDias={totalDias} diaPx={diaPx} hojeIdx={null} dataInicio={dataInicioEixo} />
                          <BarraAtividade
                            linha={linha}
                            dataInicioEixo={dataInicioEixo}
                            diaPx={diaPx}
                            onClique={() => setAtividadeSelecionada(linha)}
                            onEditarDatas={(inicioPrevisto, fimPrevisto) =>
                              editarDatas.mutate({ atividadeId: linha.atividadeId, inicioPrevisto, fimPrevisto })
                            }
                          />
                        </div>
                      ))}
                  </div>
                );
              })}
              <SetasDependencia
                linhas={linhasVisiveis}
                linhaTopoPorId={linhaTopoPorId}
                dataInicioEixo={dataInicioEixo}
                diaPx={diaPx}
                alturaLinha={alturaLinha}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-[11px] text-grafite">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-3.5 rounded bg-aco" />
          em andamento
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-3.5 rounded bg-status-verde" />
          concluída
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-3.5 rounded border-[1.5px] border-status-ambar bg-status-ambar/20" />
          conflito de precedência
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-0.5 bg-aco-700" />
          hoje
        </span>
      </div>

      {atividadeSelecionada && (
        <PainelDetalheAtividade linha={atividadeSelecionada} projetoId={projetoId} onFechar={() => setAtividadeSelecionada(null)} />
      )}
    </div>
  );
}

// SPEC-UX §4.5 — trilho de estado do projeto + trilho de fases; critério de
// gate com evidência via ModalGateReview (tela de decisão dedicada); aba
// própria de Baseline com versões e a vigente destacada.
export function ProjetoDetalhe() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [transicaoAtiva, setTransicaoAtiva] = useState<TransicaoDisponivel | null>(null);
  const [faseGateAtiva, setFaseGateAtiva] = useState<string | null>(null);
  const [aba, setAba] = useState<'fases' | 'baseline' | 'gantt'>('fases');

  const { data: projeto } = useQuery({ queryKey: ['projeto', id], queryFn: () => projetosApi.buscar(id!), enabled: !!id });
  const { data: fases } = useQuery({ queryKey: ['projeto', id, 'fases'], queryFn: () => projetosApi.fases(id!), enabled: !!id });
  const { data: transicoes } = useQuery({
    queryKey: ['projeto', id, 'transicoes'],
    queryFn: () => projetosApi.transicoesDisponiveis(id!),
    enabled: !!id,
  });

  const transicionar = useMutation({
    mutationFn: ({ evento, justificativa }: { evento: string; justificativa?: string }) => projetosApi.transicionar(id!, evento, justificativa),
    onSuccess: () => {
      setTransicaoAtiva(null);
      queryClient.invalidateQueries({ queryKey: ['projeto', id] });
    },
  });

  const submeterAoGate = useMutation({
    mutationFn: (faseId: string) => projetosApi.submeterAoGate(faseId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projeto', id, 'fases'] }),
  });

  if (!projeto) return <div className="p-8 text-sm text-grafite">Carregando…</div>;

  const estadosTrilho = ESTADOS_PROJETO.map((nome) => ({
    nome,
    atual: nome === projeto.estado,
    percorrido: ESTADOS_PROJETO.indexOf(nome) < ESTADOS_PROJETO.indexOf(projeto.estado as never),
  }));

  return (
    <div className="flex flex-col gap-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-ink">Projeto {projeto.id}</h1>
        <p className="text-sm text-grafite">
          Sponsor: {projeto.sponsor} · Gerente: {projeto.gerente}
        </p>
      </div>

      <TrilhoEstado estados={estadosTrilho} transicoesDisponiveis={transicoes ?? []} onSelecionarTransicao={setTransicaoAtiva} />

      {transicaoAtiva && (
        <ModalTransicao
          transicao={transicaoAtiva}
          onFechar={() => setTransicaoAtiva(null)}
          onConfirmar={(justificativa) => transicionar.mutate({ evento: transicaoAtiva.evento, justificativa })}
          carregando={transicionar.isPending}
        />
      )}

      {faseGateAtiva && <ModalGateReview faseId={faseGateAtiva} onFechar={() => setFaseGateAtiva(null)} />}

      <div className="flex gap-1 border-b border-borda">
        {(['fases', 'baseline', 'gantt'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            className={`px-3 py-2 text-sm font-medium capitalize ${aba === a ? 'border-b-2 border-aco text-aco-700' : 'text-grafite'}`}
          >
            {a}
          </button>
        ))}
      </div>

      {aba === 'fases' && (
        <section>
          <ul className="flex flex-col gap-3">
            {fases?.map((fase) => (
              <li key={fase.id} className="rounded-lg border border-borda bg-superficie p-4">
                <div className="flex items-center justify-between">
                  <Link to={`/fases/${fase.id}/atividades`} className="text-sm font-medium text-ink hover:text-aco">
                    {fase.ordem}. {fase.nome}
                  </Link>
                  <span className="fonte-mono text-xs text-grafite">{fase.estado}</span>
                </div>
                <div className="mt-2 flex gap-1">
                  {ESTADOS_FASE.map((e) => (
                    <span key={e} className={`h-1 flex-1 rounded ${ESTADOS_FASE.indexOf(fase.estado) >= ESTADOS_FASE.indexOf(e) ? 'bg-aco' : 'bg-borda'}`} />
                  ))}
                </div>
                {fase.estado === 'EmAndamento' && (
                  <div className="mt-3">
                    <BotaoComBloqueio disponivel onClick={() => submeterAoGate.mutate(fase.id)}>
                      Submeter ao gate
                    </BotaoComBloqueio>
                  </div>
                )}
                {fase.estado === 'AguardandoGate' && (
                  <div className="mt-3">
                    <BotaoComBloqueio disponivel onClick={() => setFaseGateAtiva(fase.id)}>
                      Avaliar gate
                    </BotaoComBloqueio>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {aba === 'baseline' && <AbaBaseline projetoId={projeto.id} />}
      {aba === 'gantt' && <AbaGantt projetoId={projeto.id} />}
    </div>
  );
}
